import { useState } from 'react';
import {
  CasButton,
  CasRenderArrayAsRows,
  CasRenderDate,
  CasTable,
  CasErrorDisplay,
} from 'src/compass/shared/components';
import CasLoader from 'src/compass/shared/components/CasLoader/CasLoader';
import useCasBoolean from 'src/compass/shared/hooks/useCasBoolean';
import Iconify from 'src/components/iconify';
import { IGeneralObjectType } from 'src/compass/site/util';
import {
  CustomerComposeDialog,
  useCurrentUserEmployee,
  CasEmployeeSelect,
  useSiteProperties,
} from 'src/compass/application';
import { DateFormat, formatDate, getDateInMoment } from 'src/compass/shared/util/casMoment';

import ICasRenderCellProps from 'src/compass/shared/components/CasTable/ICasRenderCellProps';
import getCasTableCellValue from 'src/compass/shared/components/CasTable/getCasTableCellValue';
import RenderTouchpointFrequency, { useTouchpointCache } from './RenderTouchpointFrequency';
import { NotExcludedOption_Name, TouchpointFrequencySelect } from './TouchpointFrequencySelect';
import CustomerDialog from './CustomerDialog';
import CustomerBatchSendDialog from './CustomerBatchSendDialog';
import { sendMessageColumn } from './RenderSendMessage';
import { RenderRiskManagerName } from './RenderRiskManagerName';
import customerSearchFilter from './_internal/customerSearchFilter';

export const TOUCHPOINT_ENTITY = 'Utilities/EntityTouchpoint';

export default function TouchpointsTab() {
  const { updateTouchpointListing } = useTouchpointCache();
  const [dialogRowId, setDialogRowId] = useState<number | null>(null);
  const [reloadTable, setReloadTable] = useState<Date | null>(null);
  const [composeEntityId, setComposeEntityId] = useState<number | null>(null);
  const batchSendDialog = useCasBoolean();
  // The listing's own url + filters, so Batch Send mails exactly the rows these filters match.
  const [requestMetadata, setRequestMetadata] = useState<IGeneralObjectType>();

  const { data, isLoading, isError, error } = useCurrentUserEmployee();

  // Send and Batch Send stay hidden until the Show Customer Send feature flag is on - see CustomersTab.
  const { featureFlags, isLoading: isSitePropertiesLoading } = useSiteProperties();
  const showSend = !!featureFlags.showCustomerSend;

  // Waits on the flag too: CasTable reads its columns once, at mount.
  if (isLoading || isSitePropertiesLoading) return <CasLoader />;
  if (isError) return <CasErrorDisplay message={error.message} />;

  return (
    <>
      <CasTable
        entity={TOUCHPOINT_ENTITY}
        setRequestMetadata={setRequestMetadata}
        includeExport={false}
        refreshBtn
        update={reloadTable}
        columns={[
          {
            field: 'nextTouchpointDate',
            label: 'Next Touchpoint',
            render: (props: ICasRenderCellProps) =>
              props.row?._isPending ? '(updating)' : CasRenderDate(props),
            sx: (row: Record<string, any>) =>
              row.nextTouchpointDate <= formatDate(getDateInMoment(), DateFormat.YMD)
                ? { backgroundColor: '#fde53e' }
                : {},
          },
          {
            field: 'name',
            render: (props: ICasRenderCellProps) => (
              <RenderRiskManagerName {...props} onClick={setDialogRowId} />
            ),
          },
          {
            field: 'contacts',
            sortable: false,
            render: CasRenderArrayAsRows,
          },
          {
            field: 'touchpointFrequency',
            label: 'Freq',
            render: ({ row, column }: ICasRenderCellProps) => {
              const defaultValue = getCasTableCellValue({ row, column });
              return (
                <RenderTouchpointFrequency
                  defaultValue={defaultValue}
                  id={row.id}
                  onUpdated={(nextTouchpointDate) =>
                    updateTouchpointListing({ id: row.id, nextTouchpointDate})
                  }
                />
              );
            },
          },
          {
            field: 'lastTouchpointDate',
            label: 'Last Touchpoint',
            render: CasRenderDate,
          },
          ...(showSend ? [sendMessageColumn(setComposeEntityId)] : []),
        ]}
        /* CasTable appends the full-screen toggle AFTER whatever it is given, which is what puts
           Batch Send immediately to its left. */
        toolbarButtons={
          showSend
            ? [
                <CasButton
                  key="batchSend"
                  startIcon={<Iconify icon="ic:outline-forum" />}
                  onClick={batchSendDialog.setTrue}
                >
                  Batch Send
                </CasButton>,
              ]
            : []
        }
        filters={[
          customerSearchFilter,
          {
            type: 'component',
            name: 'employeeId',
            label: 'RMC',
            initialValue: data?.employeeId,
            component: CasEmployeeSelect,
            componentProps: { params: { meAndMyEmps: true } },
          },
          {
            type: 'component',
            name: 'frequency',
            initialValue: NotExcludedOption_Name,
            component: TouchpointFrequencySelect,
          },
        ]}
      />
      {batchSendDialog.value && requestMetadata && (
        <CustomerBatchSendDialog
          onClose={batchSendDialog.setFalse}
          requestMetadata={requestMetadata}
        />
      )}
      {composeEntityId && (
        <CustomerComposeDialog
          entityId={composeEntityId}
          onClose={() => setComposeEntityId(null)}
        />
      )}
      {dialogRowId && (
        <CustomerDialog
          id={dialogRowId}
          onClose={(dataUpdated?: boolean) => {
            setDialogRowId(null);
            if (dataUpdated) setReloadTable(new Date());
          }}
        />
      )}
    </>
  );
}
